/**
 * pricingIntent — Persists a user's pricing intent across the registration flow.
 *
 * When a logged-out user clicks a pricing CTA, we store their intent
 * (which plan, which page they came from) so that after registration →
 * verification → login → onboarding, they land back on the pricing page
 * instead of going straight to /platform as a free user.
 *
 * Storage: localStorage (survives the email-verification tab switch)
 */

const STORAGE_KEY = "midash_pricing_intent";

export interface PricingIntent {
  /** The page the user was on: "/taaft", "/plans", etc. */
  source: string;
  /** Which plan they clicked: "pro", "pro_plus", "free_trial", etc. */
  planKey: string;
  /** What they clicked: "trial", "subscribe", "cta" */
  action: "trial" | "subscribe" | "cta";
  /**
   * For "subscribe" intents: the Stripe price ID to check out post-login.
   * Encodes monthly vs annual, so the post-auth router can open Stripe Checkout
   * directly instead of bouncing the user back to the pricing page.
   */
  priceId?: string;
  /** For a subscribe-with-trial intent: trial days to pass to checkout. */
  trialDays?: number;
  /** When this was stored (for expiry) */
  timestamp: number;
}

/** Max age: 24 hours (covers slow email verification) */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Store the user's pricing intent before redirecting to registration.
 */
export function storePricingIntent(intent: Omit<PricingIntent, "timestamp">): void {
  try {
    const full: PricingIntent = { ...intent, timestamp: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(full));
  } catch {
    // localStorage unavailable
  }
}

/**
 * Retrieve and clear the stored pricing intent (if still valid).
 * Returns null if expired or missing.
 */
export function consumePricingIntent(): PricingIntent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    localStorage.removeItem(STORAGE_KEY);

    const intent: PricingIntent = JSON.parse(raw);
    if (Date.now() - intent.timestamp > MAX_AGE_MS) return null;

    return intent;
  } catch {
    return null;
  }
}

/**
 * Drop every per-browser "pending CTA" intent: the pricing intent, the trial
 * signup-intent backup, and the campaign coupon.
 *
 * These all live in localStorage (per BROWSER, not per ACCOUNT), so without an
 * explicit purge one user's un-consumed CTA can fire for the NEXT account that
 * logs in on the same machine — e.g. a stale Lite subscribe intent opening a
 * checkout for an already-subscribed user. Called on logout and whenever a
 * post-auth router decides an intent must not be acted on.
 *
 * The trial-intent and coupon keys are owned by trialFlow.ts / the /taaft pages;
 * they are cleared here by literal (rather than importing) to keep this module
 * dependency-free so low-level services like tokenManager can import it safely.
 */
export function clearFunnelIntents(): void {
  for (const key of [
    STORAGE_KEY, // midash_pricing_intent
    "midash_trial_intent",
    "midash_coupon",
    "midash_trial_nudge_pending",
  ]) {
    try {
      localStorage.removeItem(key);
    } catch {
      // localStorage unavailable — best-effort.
    }
  }
}

/**
 * Get the redirect URL for a pricing intent.
 * - /taaft source → /taaft (scrolls to pricing via hash)
 * - anything else → /plans
 */
export function getIntentRedirectUrl(intent: PricingIntent): string {
  if (intent.source === "/taaft") {
    return "/taaft#taaft-pricing";
  }
  return "/plans";
}
